import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { collections, stories, getRange, getProductsForRange, rangePath, RangeKind } from '../data/mockData';
import { ProductCard } from '../components/ProductCard';
import { SectionHeading } from '../components/SectionHeading';
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * ONE story template, TWO content flavours.
 *  · kind="collection" → official artist / heritage range (Van Gogh, Morris…)
 *  · kind="story"      → Choose Your Story, South African place / mood
 * Same layout system, different labelling and tone so the two paths never blur.
 */
export function RangeStory({
  kind
}: {
  kind: RangeKind;
}) {
  const {
    id
  } = useParams();
  const range = getRange(id);
  const isStory = kind === 'story';
  const siblings = (isStory ? stories : collections).filter(r => r.id !== id);
  if (!range || range.kind !== kind) {
    return <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-40 text-center" data-id="element-736" data-mp="10fy0z2wzwwbo">
        <p className="type-display text-4xl text-coffee" data-id="element-737" data-mp="15e97b3o30op5">
          We could not find that one.
        </p>
        <Link to="/shop" className="type-cta mt-8 rounded-full bg-coffee px-8 py-4 text-[11px] text-paper" data-id="element-738" data-mp="rd6oczsdvuig">
          Back to shop
        </Link>
      </div>;
  }
  const rangeProducts = getProductsForRange(range.id);
  const label = isStory ? 'Choose Your Story' : 'Collection';
  return <div className="w-full bg-paper" data-id="element-739" data-mp="mgg6q80xkqux">
      {/* Hero */}
      <section className="relative w-full" data-id="element-740" data-mp="7r0hwxkymvhv">
        <div className="relative h-[62vh] min-h-[420px] w-full overflow-hidden" data-id="element-741" data-mp="7s25g1j5xagh">
          <img src={range.heroImage} alt={range.name} className="h-full w-full object-cover" data-id="element-742" data-mp="cq3lsep7evo9" />
          <div className="absolute inset-0 bg-gradient-to-t from-coffee/90 via-coffee/35 to-coffee/10" data-id="element-743" data-mp="3onp19rbas2i" />
          <div className="absolute inset-x-0 bottom-0" data-id="element-744" data-mp="25dt8x44ag7ng">
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: EASE
          }} className="mx-auto max-w-[1400px] px-6 pb-14" data-id="element-745" data-mp="25fe1io71lgio">
              <span className="type-eyebrow mb-5 inline-block rounded-full px-4 py-2 text-[10px]" style={isStory ? {
              backgroundColor: '#432C47',
              color: '#F3EEE7'
            } : {
              backgroundColor: '#F3EEE7',
              color: '#3B302F'
            }} data-id="element-746" data-mp="1rmofijrikblk">
                {label}
              </span>
              <h1 className="type-display text-5xl text-paper md:text-7xl" data-id="element-747" data-mp="2fdtt0s9zersp">
                {range.name}
              </h1>
              <p className="type-body mt-4 max-w-xl text-lg text-paper/75" data-id="element-748" data-mp="13xdd6xuim95b">
                {range.tagline}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mx-auto flex max-w-[1400px] items-center gap-2 px-6 py-6" data-id="element-749" data-mp="3alvsi1cfi44g">
          <Link to="/shop" className="type-body flex items-center gap-1.5 text-xs text-ink/45 transition-colors hover:text-coffee" data-id="element-750" data-mp="3fjxlbdznpht4">
            <ArrowLeftIcon className="h-3 w-3" strokeWidth={1.5} data-id="element-751" data-mp="3jjobbfhcztwl" />
            Shop
          </Link>
          <span className="text-ink/25" data-id="element-752" data-mp="3oelsxaspfbl3">/</span>
          <span className="type-body text-xs text-ink/45" data-id="element-753" data-mp="3tcdgxtq8adk0">{label}</span>
          <span className="text-ink/25" data-id="element-754" data-mp="25autgp8yxgn">/</span>
          <span className="type-body text-xs text-coffee" data-id="element-755" data-mp="23zmd3kux1j7">{range.name}</span>
        </div>
      </section>

      {/* The story */}
      <section className="mx-auto max-w-[1400px] px-6 pb-20 pt-6 md:pb-28" data-id="element-756" data-mp="729ndut9iwcq">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.15fr] lg:gap-24" data-id="element-757" data-mp="b5fpqrd983dx">
          <div data-id="element-758" data-mp="28wse9q8kbq0a">
            <p className="type-eyebrow mb-6 text-[11px] text-pennant" data-id="element-759" data-mp="28xbk48kxunxy">
              {isStory ? 'The place' : 'The archive'}
            </p>
            <p className="type-display text-3xl text-coffee md:text-[2.6rem]" data-id="element-760" data-mp="1hn5pses7kff9">
              {range.series}
            </p>
          </div>
          <div data-id="element-761" data-mp="1dkita0kle6bq">
            <p className="type-body text-lg text-ink/75" data-id="element-762" data-mp="1dl0xpkherquq">{range.longStory}</p>
            <div className="mt-10 border-l-2 pl-6" style={{
            borderColor: range.accent
          }} data-id="element-763" data-mp="1rllekb3rjcll">
              <p className="type-eyebrow mb-2 text-[10px] text-coffee/50" data-id="element-764" data-mp="1mlz3o8beq8fy">
                Inspiration
              </p>
              <p className="type-body text-base text-ink/65" data-id="element-765" data-mp="yxfvdsscxvnv">
                {range.inspiration}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pull quote */}
      {range.quote && <section className="w-full py-20 md:py-28" style={{
      backgroundColor: range.accent
    }} data-id="element-766" data-mp="18ok9kqa7lchn">
          <div className="mx-auto max-w-4xl px-6 text-center" data-id="element-767" data-mp="g66kbf2xuyu6">
            <blockquote className="type-display text-3xl text-paper md:text-5xl" data-id="element-768" data-mp="g4bzs1vkx8qm">
              “{range.quote}”
            </blockquote>
            {range.quoteAttribution && <p className="type-eyebrow mt-8 text-[10px] text-paper/70" data-id="element-769" data-mp="2mtnetme1asye">
                {range.quoteAttribution}
              </p>}
          </div>
        </section>}

      {/* Artist / maker */}
      {range.artist && <section className="w-full bg-paper-warm py-20 md:py-28" data-id="element-770" data-mp="2drxokjvw2027">
          <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20" data-id="element-771" data-mp="2hvvm4q5kf2t5">
            <div className="overflow-hidden rounded-4xl" data-id="element-772" data-mp="35itzheye1umu">
              <img src={range.artist.portrait} alt={range.artist.name} className="aspect-[4/5] w-full object-cover" data-id="element-773" data-mp="3aiyl1je5lfjx" />
            </div>
            <div data-id="element-774" data-mp="31ia5gk94374x">
              <p className="type-eyebrow mb-6 text-[11px] text-pennant" data-id="element-775" data-mp="31hryototpair">
                {isStory ? 'The maker' : 'The artist'}
              </p>
              <h2 className="type-display text-4xl text-coffee md:text-5xl" data-id="element-776" data-mp="1q468r6b534w6">
                {range.artist.name}
              </h2>
              <p className="type-body mt-2 text-sm text-ink/50" data-id="element-777" data-mp="3rsaiqziu640z">
                {range.artist.role}
              </p>
              <p className="type-body mt-7 max-w-xl text-base text-ink/70" data-id="element-778" data-mp="3musl45a9rb8a">
                {range.artist.bio}
              </p>
              <p className="type-body mt-6 max-w-xl text-base italic text-ink/55" data-id="element-779" data-mp="3i16460x2ol6o">
                “{range.artist.inspiration}”
              </p>
            </div>
          </div>
        </section>}

      {/* Products */}
      <section className="w-full bg-paper-warm py-20 md:py-28" data-id="element-780" data-mp="34415ilzouolg">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-781" data-mp="345udc4m1fev1">
          <SectionHeading eyebrow="The cups" title={`Shop ${range.name}`} subtitle={`${rangeProducts.length} ${rangeProducts.length === 1 ? 'cup' : 'cups'} in this ${isStory ? 'story' : 'collection'}.`} data-id="element-782" data-mp="2z5psk2e3jxz0" />
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-783" data-mp="tb6mul7scr3v">
            {rangeProducts.map((p, i) => <ProductCard key={p.id} product={p} index={i} data-id="element-784" data-mp="2b8a13i3yh6sx" data-mpi={i} />)}
          </div>
        </div>
      </section>

      {/* Siblings — stays within the same discovery layer */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:py-28" data-id="element-785" data-mp="2kymkh701atxh">
        <SectionHeading eyebrow={isStory ? 'More stories' : 'More collections'} title={isStory ? 'Other South African stories.' : 'Other official ranges.'} data-id="element-786" data-mp="2p0q7msw2fmyl" />
        <div className="mt-12 grid gap-6 sm:grid-cols-3" data-id="element-787" data-mp="1x9abo88mcnvp">
          {siblings.map((r, i) => <motion.div key={r.id} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.06,
          ease: EASE
        }} data-id="element-788" data-mp="21b5ejh6779bl" data-mpi={i}>
              <Link to={rangePath(r)} className="group block" data-id="element-789" data-mp="3hpezs2f58hy7">
                <div className="overflow-hidden rounded-4xl" data-id="element-790" data-mp="3hnu60f0nl31e">
                  <img src={r.image} alt={r.name} className="aspect-[5/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" data-id="element-791" data-mp="2tcblcb7jq8na" />
                </div>
                <h3 className="type-display mt-5 text-2xl text-coffee" data-id="element-792" data-mp="326zuwdk5mchw">
                  {r.name}
                </h3>
                <p className="type-body mt-1 text-sm text-ink/60" data-id="element-793" data-mp="3c32vvg39s539">
                  {r.tagline}
                </p>
                <span className="type-cta mt-3 inline-flex items-center gap-1.5 text-[10px] text-pennant" data-id="element-794" data-mp="3751c0sy0s1qq">
                  {isStory ? 'Read the story' : 'Explore'}
                  <ArrowRightIcon className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} data-id="element-795" data-mp="3h2fsgsjy0qj1" />
                </span>
              </Link>
            </motion.div>)}
        </div>
      </section>
    </div>;
}